import React, { useMemo, useState } from 'react';
import {
  MessageCircle,
  Plus,
  Search,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  Bot,
  X,
} from 'lucide-react';
import type { Conversation, User as UserType } from '../types';

interface ConversationListProps {
  conversations: Conversation[];
  activeId?: string;
  currentUser: UserType | null;
  loading?: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  /** Whether the sidebar is collapsed to icon-rail (desktop) */
  isCollapsed: boolean;
  /** Whether the mobile overlay is open */
  isMobileOpen: boolean;
  /** Close the mobile overlay */
  onMobileClose: () => void;
  /** Toggle collapsed state (desktop) */
  onToggleCollapse: () => void;
}

export const ConversationList = React.memo(function ConversationList({
  conversations,
  activeId,
  currentUser,
  loading,
  onSelect,
  onNew,
  isCollapsed,
  isMobileOpen,
  onMobileClose,
  onToggleCollapse,
}: ConversationListProps) {
  const [query, setQuery] = useState('');

  const filteredConversations = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return conversations;
    return conversations.filter((c) =>
      c.title?.toLowerCase().includes(trimmed),
    );
  }, [conversations, query]);

  const sortedConversations = useMemo(
    () =>
      [...filteredConversations].sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      ),
    [filteredConversations],
  );

  const groupedConversations = useMemo(() => {
    const today: Conversation[] = [];
    const week: Conversation[] = [];
    const older: Conversation[] = [];
    const now = new Date();
    const isToday = (d: Date) => d.toDateString() === now.toDateString();
    const isThisWeek = (d: Date) =>
      now.getTime() - d.getTime() < 7 * 24 * 60 * 60 * 1000;

    sortedConversations.forEach((c) => {
      const d = new Date(c.updated_at);
      if (isToday(d)) today.push(c);
      else if (isThisWeek(d)) week.push(c);
      else older.push(c);
    });

    return [
      { label: 'Today', items: today },
      { label: 'This Week', items: week },
      { label: 'Earlier', items: older },
    ].filter((g) => g.items.length > 0);
  }, [sortedConversations]);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const handleSelect = (id: string) => {
    onSelect(id);
    onMobileClose();
  };

  const handleNew = () => {
    onNew();
    onMobileClose();
  };

  // ─── Collapsed icon-rail (render helper, NOT a component) ─────────────────
  const collapsedRail = (
    <div className="hidden lg:flex flex-col h-full w-[60px] bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex-shrink-0">
      {/* Logo mark */}
      <div className="flex items-center justify-center h-[52px] border-b border-gray-200 dark:border-gray-700">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-sm">
          <Bot className="w-4 h-4 text-white" />
        </div>
      </div>

      <div className="flex flex-col items-center py-3 gap-2 flex-1 overflow-hidden">
        {/* New conversation */}
        <button
          onClick={handleNew}
          disabled={loading}
          className="w-9 h-9 rounded-lg bg-sky-600 text-white flex items-center justify-center hover:bg-sky-700 transition-colors shadow-sm disabled:opacity-50"
          title="New conversation"
          data-testid="new-conversation-collapsed"
        >
          <Plus className="w-4 h-4" />
        </button>

        {/* Recent conversation dots */}
        <div className="flex flex-col gap-1.5 mt-1 flex-1 overflow-hidden">
          {sortedConversations.slice(0, 8).map((conv) => (
            <button
              key={conv.id}
              onClick={() => handleSelect(conv.id)}
              title={conv.title || 'Untitled'}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                activeId === conv.id
                  ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400'
                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-200'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>

      {/* Expand button */}
      <div className="flex items-center justify-center h-12 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onToggleCollapse}
          className="w-9 h-9 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-colors"
          title="Expand sidebar"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // ─── Expanded sidebar content (render helper, NOT a component) ─────────────
  const renderSidebarContent = (onClose?: () => void) => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 h-[52px] border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-sm">
            <Bot className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm">CXS Agents</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex w-7 h-7 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 items-center justify-center transition-colors"
            title="Collapse sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden w-7 h-7 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
              aria-label="Close sidebar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* New conversation button */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        <button
          onClick={handleNew}
          disabled={loading}
          className="w-full bg-sky-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-sky-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
          data-testid="new-conversation-button"
        >
          <Plus className="w-4 h-4" />
          New conversation
        </button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2 flex-shrink-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-500"
          />
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto px-2">
        {groupedConversations.length === 0 ? (
          <div className="py-8 text-center px-2">
            {query ? (
              <p className="text-xs text-gray-500">No matches for "{query}"</p>
            ) : (
              <p className="text-xs text-gray-400">No conversations yet</p>
            )}
          </div>
        ) : (
          groupedConversations.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {group.label}
              </div>
              {group.items.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => handleSelect(conversation.id)}
                  className={`w-full text-left px-2 py-2 rounded-lg mb-0.5 transition-colors group ${
                    activeId === conversation.id
                      ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageCircle
                      className={`w-3.5 h-3.5 flex-shrink-0 ${
                        activeId === conversation.id
                          ? 'text-sky-500'
                          : 'text-gray-400'
                      }`}
                    />
                    <span className="flex-1 text-sm truncate font-medium">
                      {conversation.title || 'Untitled'}
                    </span>
                    <span className="flex-shrink-0 text-[10px] text-gray-400 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Clock className="w-2.5 h-2.5" />
                      {formatRelativeTime(conversation.updated_at)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ))
        )}
      </div>

      {/* User info */}
      {currentUser && (
        <div className="px-3 py-2.5 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <User className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                {currentUser.name}
              </p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                {currentUser.org_name}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isCollapsed) {
    return collapsedRail;
  }

  return (
    <>
      {/* Desktop persistent sidebar */}
      <div
        className="hidden lg:flex flex-col h-full w-[240px] bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex-shrink-0"
        data-testid="conversation-sidebar"
      >
        {renderSidebarContent()}
      </div>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onMobileClose}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-0 h-full w-72 bg-white dark:bg-gray-900 shadow-xl z-10 flex flex-col">
            {renderSidebarContent(onMobileClose)}
          </div>
        </div>
      )}
    </>
  );
});
